// Phase HR-003 Wave 3 — Raise-Grievance modal.
//
// Used in two places:
//   1. Header chip on My-To-Do (worker raising about own situation)
//   2. Inbox page "Raise on behalf" button (HR raising on behalf of someone)
//
// Anonymity tradeoffs explained in-line to the operator so they understand
// what HR will and won't see.

import { useEffect, useState } from 'react'
import {
  Alert, App, Form, Input, Modal, Radio, Select, Space, Switch, Tag,
} from 'antd'
import { useMutation, useQuery } from '@tanstack/react-query'
import { grievanceApi, grievanceCategoryApi } from '@/features/hr/grievanceApi'
import type { GrievanceSeverity } from '@/features/hr/grievanceApi'
import { departmentsApi } from '@/features/masters/departments/api'
import { usePlantId } from '@/shared/hooks/usePlantId'

interface Props {
  open: boolean
  onClose: () => void
  onRaised?: () => void
  defaultDepartmentId?: string
  defaultSupervisorId?: string
}

const SEVERITY_OPTIONS: { value: GrievanceSeverity; label: string; color: string }[] = [
  { value: 'LOW',      label: 'Low',      color: 'default' },
  { value: 'MEDIUM',   label: 'Medium',   color: 'blue' },
  { value: 'HIGH',     label: 'High',     color: 'orange' },
  { value: 'CRITICAL', label: 'Critical', color: 'red' },
]

export default function RaiseGrievanceModal({
  open, onClose, onRaised, defaultDepartmentId, defaultSupervisorId,
}: Props) {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const [form] = Form.useForm()
  const [isAnon, setAnon] = useState(false)
  const [severity, setSeverity] = useState<GrievanceSeverity>('MEDIUM')

  const { data: categories } = useQuery({
    queryKey: ['grievance-categories'],
    queryFn: grievanceCategoryApi.list,
    enabled: open,
  })
  const { data: deptsData } = useQuery({
    queryKey: ['hr-grievance-depts', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 500, plant_id: plantId }),
    enabled: open && !!plantId,
  })

  useEffect(() => {
    if (open) {
      form.resetFields()
      setAnon(false)
      setSeverity('MEDIUM')
      if (defaultDepartmentId) form.setFieldValue('department_id', defaultDepartmentId)
      if (defaultSupervisorId) form.setFieldValue('about_supervisor_id', defaultSupervisorId)
    }
  }, [open, defaultDepartmentId, defaultSupervisorId, form])

  const raiseMut = useMutation({
    mutationFn: grievanceApi.raise,
    onSuccess: (g) => {
      message.success(`Grievance ${g.grievance_no} raised.`)
      onRaised?.()
      onClose()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => {
      const detail = e?.response?.data?.detail ?? 'Failed to raise grievance'
      message.error(typeof detail === 'string' ? detail : 'Failed to raise grievance')
    },
  })

  const onFinish = (values: Record<string, unknown>) => {
    if (!plantId) {
      message.error('Plant context missing — log in again or pick a plant.')
      return
    }
    raiseMut.mutate({
      plant_id: plantId,
      category_id: values.category_id as string,
      description: values.description as string,
      severity,
      is_anonymous: isAnon,
      department_id: (values.department_id as string) || null,
      about_supervisor_id: (values.about_supervisor_id as string) || null,
      witness_name: (values.witness_name as string) || null,
    })
  }

  return (
    <Modal
      title="Raise a grievance"
      open={open}
      onCancel={onClose}
      width={560}
      onOk={() => form.submit()}
      okText="Submit"
      confirmLoading={raiseMut.isPending}
    >
      {/* Anonymity tradeoffs — be explicit. Doc Section 12 recommended
          this as a transparency rule. */}
      <Alert
        type={isAnon ? 'warning' : 'info'}
        showIcon
        message={isAnon
          ? 'Anonymous mode — your supervisor cannot see who raised this. HR can still see your name to follow up.'
          : 'Your supervisor and HR will see your name on this grievance.'}
        style={{ marginBottom: 12 }}
      />
      <Form form={form} layout="vertical" onFinish={onFinish}
            initialValues={{ severity: 'MEDIUM' }}>
        <Form.Item label="Category" name="category_id"
                   rules={[{ required: true, message: 'Pick a category' }]}>
          <Select placeholder="Pick a category"
                  options={(categories ?? []).map((c) => ({
                    label: c.category_name, value: c.id,
                  }))} />
        </Form.Item>

        <Form.Item label="Severity">
          <Radio.Group value={severity}
                       onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITY_OPTIONS.map((s) => (
              <Radio.Button key={s.value} value={s.value}>
                <Tag color={s.color} style={{ margin: 0 }}>{s.label}</Tag>
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>

        <Form.Item label="What happened?" name="description"
                   rules={[{ required: true, min: 5 }]}
                   tooltip="Be specific: when, where, who was involved.">
          <Input.TextArea rows={4}
                          placeholder="Describe the issue in your own words…" />
        </Form.Item>

        <Form.Item label="Department" name="department_id">
          <Select allowClear placeholder="Pick the relevant department (optional)"
                  options={(deptsData?.items ?? []).map((d) => ({
                    label: d.name, value: d.id,
                  }))} />
        </Form.Item>

        <Form.Item label="Witness name (optional)" name="witness_name">
          <Input placeholder="Someone who can corroborate" />
        </Form.Item>

        <Space style={{ display: 'flex', alignItems: 'center' }}>
          <Switch checked={isAnon} onChange={setAnon} />
          <span style={{ fontSize: 13, fontWeight: 500 }}>
            Raise anonymously
          </span>
        </Space>
      </Form>
    </Modal>
  )
}
